// src/components/SellerDashboardView.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserProfile, MarketplaceItem, Offer, ToastType } from '../types';
import { formatCurrency } from '../../i18n';
import { MarketplaceIcon, TrashIcon, CheckIcon, XIcon } from './icons';
import { FEATURE } from '../constants/features';

import { db } from '../lib/firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';

export interface EscrowInfo {
  transactionId: string;
  amountTotal: number; // cents
  currency: string;
  escrowStatus?: 'HELD' | 'RELEASED' | 'PENDING' | 'PAID_OUT' | string;
}

interface SellerDashboardViewProps {
  userProfile: UserProfile;
  marketplaceItems: MarketplaceItem[];
  onItemUpdated: (itemId: MarketplaceItem['id'], patch: Partial<MarketplaceItem>) => void | Promise<void>;
  onCancel?: () => void;
  onItemDeleted: (itemId: MarketplaceItem['id']) => void | Promise<void>;

  // ✅ UPDATED: allow counter + optional payload
  onOfferAction: (
    itemId: MarketplaceItem['id'],
    offerId: string,
    action: 'accept' | 'reject' | 'counter',
    payload?: { message?: string; amountCents?: number },
  ) => void | Promise<void>;

  currency: string; // kept for signature, but UI is locked to USD
  escrowByItemId?: Record<string, EscrowInfo>;
  onReleaseEscrow?: (payload: string | { transactionId: string; itemId: MarketplaceItem['id'] }) => void | Promise<void>;
  addToast: (message: string, type?: ToastType) => void;

  // allows focusing a specific offer when arriving from notification
  viewState?: { targetId?: MarketplaceItem['id']; subTargetId?: string };

  // optional — still supported
  onEditListing?: (item: MarketplaceItem) => void | Promise<void>;
  isDarkMode?: boolean;
}

const SellerDashboardView: React.FC<SellerDashboardViewProps> = ({
  userProfile,
  marketplaceItems,
  onItemUpdated,
  onCancel,
  onItemDeleted,
  onOfferAction,
  escrowByItemId = {},
  onReleaseEscrow,
  addToast,
  viewState,
  onEditListing,
  isDarkMode = false,
}) => {
  const { t, i18n } = useTranslation();

  const myItems = useMemo(
    () => marketplaceItems.filter((i) => i.sellerId === userProfile.uid),
    [marketplaceItems, userProfile.uid],
  );

  const activeCount = myItems.filter((i) => i.status !== 'sold').length;

  const moneyFromCents = (cents: number) =>
    formatCurrency((Number(cents) || 0) / 100, 'USD', i18n.language);

  const totalSalesCents = myItems.reduce((sum, item) => {
    const escrow = escrowByItemId[String(item.id)];
    if (escrow?.amountTotal) return sum + escrow.amountTotal;
    if (item.status === 'sold') return sum + (item.priceCents || 0);
    return sum;
  }, 0);

  // ✅ price helper: ALWAYS return cents
  const getDisplayPriceCents = (item: any): number => {
    if (typeof item?.priceCents === 'number') return item.priceCents;
    if (typeof item?.price === 'number') return Math.round(item.price * 100); // legacy dollars -> cents
    return 0;
  };

  const handleBack = () => {
    if (typeof onCancel === 'function') return onCancel();
    if (window.history.length > 1) return window.history.back();
    window.location.href = '/app';
  };
  
  // ─────────────────────────────────────────────
  // ✅ Live offers (subcollection) per item
  // ─────────────────────────────────────────────
  const [offersByItemId, setOffersByItemId] = useState<Record<string, Offer[]>>({});
  const offerUnsubsRef = useRef<Record<string, () => void>>({});

  useEffect(() => {
    // cleanup old listeners
    Object.values(offerUnsubsRef.current).forEach((u) => {
      try {
        u();
      } catch {}
    });
    offerUnsubsRef.current = {};
    setOffersByItemId({});

    myItems.forEach((item) => {
      const itemIdStr = String(item.id);
      const offersRef = collection(db as any, 'marketplaceItems', itemIdStr, 'offers');
      const q = query(offersRef as any, orderBy('createdAt', 'desc'));

      const unsub = onSnapshot(
        q as any,
        (snap) => {
          // expect docs already contain id field based on your offer writer,
          // but we’ll also fallback to doc.id if needed.
          const offers = snap.docs
            .map((d) => {
              const data = d.data() as any;
              return { ...data, id: data?.id || d.id };
            })
            .filter(Boolean);

          setOffersByItemId((prev) => ({ ...prev, [itemIdStr]: offers as any }));
        },
        (err) => console.error('[SellerDashboard] offers listener error', itemIdStr, err),
      );

      offerUnsubsRef.current[itemIdStr] = unsub;
    });

    return () => {
      Object.values(offerUnsubsRef.current).forEach((u) => {
        try {
          u();
        } catch {}
      });
      offerUnsubsRef.current = {};
    };
  }, [myItems]);

  // ─────────────────────────────────────────────
  // ✅ Escrow + listing actions
  // ─────────────────────────────────────────────
  const handleReleaseClick = async (itemId: MarketplaceItem['id']) => {
    if (!FEATURE.BAZAAR_PAYMENTS_ENABLED || !onReleaseEscrow) return;
    const escrow = escrowByItemId[String(itemId)];
    if (!escrow) return;

    try {
      await onReleaseEscrow({ transactionId: escrow.transactionId, itemId });
      addToast(t('seller.payoutReleased', 'Payout released'), 'success');
    } catch (e) {
      console.error(e);
      addToast(t('errors.unableToReleasePayout', 'Unable to release payout.'), 'error');
    }
  };

  const handleDelete = async (itemId: MarketplaceItem['id']) => {
    try {
      await onItemDeleted(itemId);
      addToast(t('seller.itemDeleted', 'Listing deleted'), 'success');
    } catch (e) {
      console.error(e);
      addToast(t('errors.unableToDeleteItem', 'Unable to delete listing.'), 'error');
    }
  };

  // ─────────────────────────────────────────────
  // ✅ Counter modal + unified offer action handler
  // ─────────────────────────────────────────────
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterItemId, setCounterItemId] = useState<MarketplaceItem['id'] | null>(null);
  const [counterOffer, setCounterOffer] = useState<Offer | null>(null);
  const [counterMessage, setCounterMessage] = useState('');
  const [counterAmount, setCounterAmount] = useState<string>(''); // dollars in UI

  const openCounter = (itemId: MarketplaceItem['id'], offer: Offer) => {
    setCounterItemId(itemId);
    setCounterOffer(offer);
    setCounterMessage('');
    setCounterAmount('');
    setCounterOpen(true);
  };

  const submitCounter = async () => {
    if (!counterItemId || !counterOffer) return;

    const amountNum = counterAmount.trim() ? Number(counterAmount) : NaN;
    const amountCents = Number.isFinite(amountNum) ? Math.round(amountNum * 100) : undefined;

    try {
      await onOfferAction(counterItemId, counterOffer.id, 'counter', {
        message: counterMessage.trim() || undefined,
        amountCents,
      });
      addToast(t('seller.counterSent', 'Counter sent'), 'success');
      setCounterOpen(false);
    } catch (e) {
      console.error(e);
      addToast(t('errors.unableToUpdateOffer', 'Unable to update offer.'), 'error');
    }
  };

  const handleOfferActionClick = async (
    itemId: MarketplaceItem['id'],
    offer: Offer,
    action: 'accept' | 'reject' | 'counter',
  ) => {
    if (action === 'counter') {
      openCounter(itemId, offer);
      return;
    }

    try {
      await onOfferAction(itemId, offer.id, action);
      addToast(
        action === 'accept'
          ? t('seller.offerAccepted', 'Offer accepted')
          : t('seller.offerRejected', 'Offer rejected'),
        'success',
      );
    } catch (e) {
      console.error(e);
      addToast(t('errors.unableToUpdateOffer', 'Unable to update offer.'), 'error');
    }
  };

  // 🔧 simplified: only cares whether escrow exists
  const renderEscrowPill = (item: MarketplaceItem) => {
    if (!FEATURE.BAZAAR_PAYMENTS_ENABLED || !onReleaseEscrow) return null;
    const escrow: EscrowInfo | undefined = escrowByItemId[String(item.id)];
    if (!escrow) return null;

    return (
      <button
        type="button"
        onClick={() => handleReleaseClick(item.id)}
        className="mt-2 inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-semibold bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_3px_0_0_rgba(4,120,87,0.9)]"
      >
        <CheckIcon className="w-3 h-3 mr-1.5" />
        {t('seller.releaseEscrow', 'Release payout')}
      </button>
    );
  };

  // ─────────────────────────────────────────────
  // ✅ Edit Listing modal (kept from your file)
  // ─────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editingItem, setEditingItem] = useState<MarketplaceItem | null>(null);

  const [editTitle, setEditTitle] = useState('');
  const [editPrice, setEditPrice] = useState(''); // dollars string
  const [editQty, setEditQty] = useState('1');
  const [editCondition, setEditCondition] = useState<'new' | 'used'>('used');
  const [editDesc, setEditDesc] = useState('');

  const openEditModal = (item: MarketplaceItem) => {
    if (onEditListing) void Promise.resolve(onEditListing(item));

    const anyItem = item as any;
    setEditingItem(item);
    setEditTitle(String(anyItem.title ?? anyItem.name ?? ''));
    const cents = getDisplayPriceCents(item);
    setEditPrice((cents / 100).toFixed(2));
    setEditQty(String(anyItem.quantity ?? 1));
    setEditCondition((anyItem.condition === 'new' ? 'new' : 'used') as 'new' | 'used');
    setEditDesc(String(anyItem.description ?? ''));
    setEditOpen(true);
  };

  const closeEditModal = () => {
    if (editSaving) return;
    setEditOpen(false);
    setEditingItem(null);
  };

  const saveEdit = async () => {
    if (!editingItem) return;

    const title = editTitle.trim();
    const qtyNum = Number(editQty.replace(/[^0-9]/g, '') || '1');
    const priceNum = Number(editPrice.replace(/[^0-9.]/g, '') || '0');

    if (!title) {
      addToast(t('bazaar.nameRequired', 'Item name is required.'), 'error');
      return;
    }
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      addToast(t('bazaar.validQuantity', 'Enter a valid quantity.'), 'error');
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      addToast(t('bazaar.validPrice', 'Enter a valid price.'), 'error');
      return;
    }

    const patch: any = {
      title,
      name: title,
      description: editDesc.trim() || undefined,
      quantity: qtyNum,
      condition: editCondition,
      price: priceNum,
      priceCents: Math.round(priceNum * 100),
      updatedAt: Date.now(),
    };

    Object.keys(patch).forEach((k) => {
      if (patch[k] === undefined) delete patch[k];
    });

    setEditSaving(true);
    try {
      await onItemUpdated(editingItem.id, patch);
      addToast(t('seller.updated', 'Listing updated'), 'success');
      closeEditModal();
    } catch (e) {
      console.error(e);
      addToast(t('errors.unableToUpdateListing', 'Unable to update listing.'), 'error');
    } finally {
      setEditSaving(false);
    }
  };

  // scroll highlight item
  useEffect(() => {
    if (!viewState?.targetId) return;
    const el = document.getElementById(`seller-item-${viewState.targetId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('animate-highlight');
    const timeout = setTimeout(() => el.classList.remove('animate-highlight'), 2000);
    return () => clearTimeout(timeout);
  }, [viewState?.targetId]);

  // scroll highlight offer (if provided)
  useEffect(() => {
    if (!viewState?.subTargetId) return;
    const el = document.getElementById(`seller-offer-${viewState.subTargetId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('animate-highlight');
    const timeout = setTimeout(() => el.classList.remove('animate-highlight'), 2000);
    return () => clearTimeout(timeout);
  }, [viewState?.subTargetId]);

  return (
    <>
      <div data-roddo-theme={isDarkMode ? 'dark' : 'light'} className={`${isDarkMode ? 'dark ' : ''}flex h-full flex-col bg-[#F7F2E8] text-[#30271E] dark:bg-[#090B13] dark:text-[#F4F6FA]`}>
        {/* HEADER */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] bg-[var(--roddo-panel-light-strong)] dark:bg-[var(--roddo-panel-dark-strong)] backdrop-blur-xl shadow-[0_6px_0_0_rgba(184,182,232,0.9)]">
          <div className="flex items-center gap-3">
          <button
  onClick={handleBack}
  type="button"
  aria-label={t('common.back', 'Back')}
  title={t('common.back', 'Back')}
  className="shrink-0 w-10 h-10 grid place-items-center text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] hover:opacity-80 active:opacity-60 transition-opacity"
>
  <span className="text-xl font-black leading-none">‹</span>
</button>

            <div className="w-9 h-9 rounded-2xl bg-[#DFF6EA] dark:bg-emerald-400/18 flex items-center justify-center shadow-[0_3px_0_0_rgba(16,185,129,0.9)]">
              <MarketplaceIcon className="w-4 h-4 text-[#17684C] dark:text-emerald-100" />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-extrabold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                {t('seller.dashboardTitle', 'Seller Dashboard')}
              </h1>
              <p className="text-[11px] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                {FEATURE.BAZAAR_PAYMENTS_ENABLED
                  ? t('seller.dashboardSubtitle', 'Manage offers, status, and payouts for your Bazaar listings.')
                  : t('seller.dashboardSubtitleListingsOnly', 'Manage listings and offers. Payments are arranged directly while Roddo Checkout is in review.')}
              </p>
            </div>
          </div>
        </header>

        {/* STATS */}
        <section className="px-4 pt-3 pb-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[var(--roddo-panel-light-strong)] dark:bg-[var(--roddo-panel-dark-strong)] rounded-2xl px-3 py-3 shadow-[0_4px_0_0_rgba(148,163,184,0.5)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)]">
              <p className="text-xs text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                {t('seller.activeListings', 'Active listings')}
              </p>
              <p className="mt-1 text-xl font-extrabold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                {activeCount}
              </p>
            </div>

            <div className="bg-[var(--roddo-panel-light-strong)] dark:bg-[var(--roddo-panel-dark-strong)] rounded-2xl px-3 py-3 shadow-[0_4px_0_0_rgba(148,163,184,0.5)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)]">
              <p className="text-xs text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                {t('seller.totalSales', 'Total sales')}
              </p>
              <p className="mt-1 text-xl font-extrabold text-[#17684C] dark:text-emerald-100">
                {moneyFromCents(totalSalesCents)}
              </p>
            </div>
          </div>
        </section>

        {/* LISTINGS */}
        {myItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-sm text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)] px-4">
            <p className="text-center max-w-xs">
              {t('seller.noListings', 'You have no active listings yet.')}
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 pb-5 space-y-4 pt-2">
            {myItems.map((item) => {
              const offers: Offer[] = offersByItemId[String(item.id)] || [];

              const primaryImage =
                (((item as any).images && (item as any).images.length > 0 ? (item as any).images[0] : null) as any) ||
                (item as any).imageUrl ||
                null;

              const displayPriceCents = getDisplayPriceCents(item);

              return (
                <div
                  key={item.id}
                  id={`seller-item-${item.id}`}
                  className="rounded-3xl border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] bg-[var(--roddo-panel-light-strong)] dark:bg-[var(--roddo-panel-dark-strong)] shadow-[0_10px_28px_rgba(0,0,0,0.22)] p-4 sm:p-5 flex flex-col gap-3"
                >
                  <div className="flex gap-3">
                    {primaryImage ? (
                      <img
                        src={primaryImage}
                        alt={(item as any).title}
                        className="w-20 h-20 rounded-2xl object-cover flex-shrink-0 border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)]"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl flex-shrink-0 bg-[var(--roddo-panel-light)] dark:bg-[var(--roddo-panel-dark)] border border-dashed border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] flex items-center justify-center text-[11px] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                        {t('seller.noImage', 'No image')}
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] truncate">
                            {(item as any).title}
                          </h3>

                          {(item as any).description && (
                            <p className="mt-1 text-xs text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)] line-clamp-2">
                              {(item as any).description}
                            </p>
                          )}

                          {(item as any).status && (
                            <p className="mt-2 text-[11px] uppercase tracking-wide text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                              {t('seller.status', 'Status')}:&nbsp;
                              <span className="font-semibold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                                {(item as any).status}
                              </span>
                            </p>
                          )}

                          {renderEscrowPill(item)}
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <span className="text-sm font-bold text-[#17684C] dark:text-emerald-100 dark:text-emerald-300">
                            {moneyFromCents(displayPriceCents)}
                          </span>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(item)}
                              className="inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-semibold bg-[var(--roddo-panel-light)] text-[var(--roddo-text-light)] border border-[var(--roddo-border-light)] shadow-[0_3px_0_0_rgba(148,163,184,0.6)] hover:bg-[var(--roddo-panel-light-strong)] dark:bg-[var(--roddo-panel-dark)] dark:text-[var(--roddo-text-dark)] dark:border-[var(--roddo-border-dark)] dark:hover:bg-[var(--roddo-panel-dark-strong)] hover:-translate-y-[1px] active:translate-y-0 active:shadow-none transition"
                            >
                              {t('seller.editListing', 'Edit')}
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="inline-flex items-center px-2.5 py-1.5 rounded-full text-[11px] font-medium text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 dark:border-red-700 dark:bg-red-900/40 dark:text-red-300"
                            >
                              <TrashIcon className="w-3 h-3 mr-1" />
                              {t('seller.deleteListing', 'Delete')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* OFFERS */}
                  {offers.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-xs font-semibold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                        {t('seller.offers', 'Offers / Inquiries')}
                      </div>

                      {offers.map((offer) => {
                        const isFocused =
                          String(viewState?.targetId ?? '') === String(item.id) &&
                          String(viewState?.subTargetId ?? '') === String((offer as any).id);

                        const status = (offer as any).status || 'pending';
                        const buyerMsg = (offer as any).message || (offer as any).note || '';
                        const sellerAction = (offer as any).sellerAction as string | undefined;
                        const sellerMsg = (offer as any).sellerMessage as string | undefined;
                        const sellerAmtCents = (offer as any).sellerAmountCents as number | undefined;

                        return (
                          <div
                            key={(offer as any).id}
                            id={`seller-offer-${(offer as any).id}`}
                            className={[
                              'rounded-2xl border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light)] dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark)] p-3',
                              isFocused
                                ? 'border-[#7c6ff7] shadow-[0_0_0_3px_rgba(124,111,247,0.25)]'
                                : 'border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)]',
                            ].join(' ')}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] truncate">
                                  {(offer as any).buyerName || t('seller.buyer', 'Buyer')}
                                </div>

                                {buyerMsg && (
                                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                                    {buyerMsg}
                                  </div>
                                )}

                                {sellerAction && (
                                  <div className="mt-2 text-xs text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                                    <span className="font-semibold">
                                      {t('seller.yourResponse', 'Your response')}:
                                    </span>{' '}
                                    {sellerAction === 'counter' ? t('seller.counter', 'Counter') : sellerAction}
                                    {typeof sellerAmtCents === 'number' ? <> • {moneyFromCents(sellerAmtCents)}</> : null}
                                    {sellerMsg ? <div className="mt-1 whitespace-pre-wrap">{sellerMsg}</div> : null}
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-col items-end gap-2">
                                <span
                                  className={[
                                    'text-[11px] px-2 py-1 rounded-full border',
                                    status === 'accepted'
                                      ? 'border-emerald-200 text-[#17684C] dark:text-emerald-100 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800'
                                      : status === 'rejected'
                                      ? 'border-rose-200 text-rose-700 bg-rose-50 dark:bg-rose-900/20 dark:text-rose-200 dark:border-rose-800'
                                      : 'border-slate-200 text-[var(--roddo-text-light)] bg-[var(--roddo-panel-light)] dark:bg-[var(--roddo-panel-dark)] dark:text-[var(--roddo-text-dark)] dark:border-[var(--roddo-border-dark)]',
                                  ].join(' ')}
                                >
                                  {status}
                                </span>

                                {status === 'pending' && (
                                  <div className="flex gap-2">
                                    <button
                                      className="h-9 px-3 rounded-xl bg-emerald-600 text-white text-xs font-semibold shadow-[0_3px_0_0_rgba(5,150,105,0.35)] active:translate-y-[1px]"
                                      onClick={() => handleOfferActionClick(item.id, offer, 'accept')}
                                      title={t('seller.accept', 'Accept')}
                                    >
                                      <CheckIcon className="w-4 h-4 inline -mt-[2px]" /> {t('seller.accept', 'Accept')}
                                    </button>

                                    <button
                                      className="h-9 px-3 rounded-xl bg-[#7c6ff7] text-white text-xs font-semibold shadow-[0_3px_0_0_rgba(124,111,247,0.35)] active:translate-y-[1px]"
                                      onClick={() => handleOfferActionClick(item.id, offer, 'counter')}
                                      title={t('seller.counter', 'Counter')}
                                    >
                                      {t('seller.counter', 'Counter')}
                                    </button>

                                    <button
                                      className="h-9 px-3 rounded-xl bg-rose-600 text-white text-xs font-semibold shadow-[0_3px_0_0_rgba(225,29,72,0.35)] active:translate-y-[1px]"
                                      onClick={() => handleOfferActionClick(item.id, offer, 'reject')}
                                      title={t('seller.reject', 'Reject')}
                                    >
                                      <XIcon className="w-4 h-4 inline -mt-[2px]" /> {t('seller.reject', 'Reject')}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ✅ EDIT MODAL */}
        {editOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" onClick={closeEditModal} />
            <div className="relative w-full max-w-lg rounded-3xl bg-[var(--roddo-panel-light-strong)] dark:bg-[var(--roddo-panel-dark-strong)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] shadow-[0_12px_0_rgba(0,0,0,0.28)] p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-base sm:text-lg font-extrabold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                    {t('seller.editTitle', 'Edit listing')}
                  </h2>
                  <p className="mt-1 text-[11px] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                    {t('seller.editSub', 'Update title, price, quantity, condition, and description.')}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeEditModal}
                  className="rounded-full px-3 py-1 text-[11px] font-semibold bg-[#7c6ff7] text-white shadow-[0_4px_0_rgba(55,47,142,0.85)]-[0_3px_0_rgba(15,23,42,0.85)] disabled:opacity-60"
                  disabled={editSaving}
                >
                  {t('common.close', 'Close')}
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder={t('bazaar.itemName', 'Item Name')}
                  className="w-full rounded-2xl bg-[var(--roddo-panel-light)] dark:bg-[var(--roddo-panel-dark)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] px-4 py-3 text-sm text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                />

                <div className="grid grid-cols-2 gap-3">
                  <input
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    placeholder={t('bazaar.price', 'Price')}
                    inputMode="decimal"
                    className="w-full rounded-2xl bg-[var(--roddo-panel-light)] dark:bg-[var(--roddo-panel-dark)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] px-4 py-3 text-sm text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                  />
                  <input
                    value={editQty}
                    onChange={(e) => setEditQty(e.target.value)}
                    placeholder={t('bazaar.quantity', 'Quantity')}
                    inputMode="numeric"
                    className="w-full rounded-2xl bg-[var(--roddo-panel-light)] dark:bg-[var(--roddo-panel-dark)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] px-4 py-3 text-sm text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                  />
                </div>

                <div className="rounded-2xl bg-[var(--roddo-panel-light)] dark:bg-[var(--roddo-panel-dark)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] px-4 py-3">
                  <label className="block text-[11px] font-semibold text-slate-600 mb-2">
                    {t('bazaar.condition', 'Condition')}
                  </label>
                  <div className="flex rounded-[12px] border border-[var(--roddo-border-light)] bg-[#FFFCF7] p-1 dark:border-[var(--roddo-border-dark)] dark:bg-[#0D1422]">
                    <button
                      type="button"
                      onClick={() => setEditCondition('used')}
                      className={`w-1/2 p-2 text-[11px] font-extrabold rounded-lg transition ${
                        editCondition === 'used'
                          ? 'bg-[#7c6ff7] text-white shadow-[0_4px_0_rgba(55,47,142,0.85)]'
                          : 'text-slate-700 hover:text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]'
                      }`}
                    >
                      {t('bazaar.conditionUsed', 'Used')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditCondition('new')}
                      className={`w-1/2 p-2 text-[11px] font-extrabold rounded-lg transition ${
                        editCondition === 'new'
                          ? 'bg-[#7c6ff7] text-white shadow-[0_4px_0_rgba(55,47,142,0.85)]'
                          : 'text-slate-700 hover:text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]'
                      }`}
                    >
                      {t('bazaar.conditionNew', 'New')}
                    </button>
                  </div>
                </div>

                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  placeholder={t('bazaar.description', 'Description')}
                  rows={4}
                  className="w-full rounded-2xl bg-[var(--roddo-panel-light)] dark:bg-[var(--roddo-panel-dark)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] px-4 py-3 text-sm text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/60 resize-none"
                />

                <div className="mt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    disabled={editSaving}
                    className="rounded-[12px] px-4 py-2 text-[11px] font-semibold bg-[var(--roddo-panel-light)] dark:bg-[var(--roddo-panel-dark)] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] text-slate-700 shadow-[0_4px_0_rgba(148,163,184,0.6)] hover:bg-slate-50 disabled:opacity-60"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={editSaving}
                    className="rounded-[12px] px-4 py-2 text-[11px] font-semibold bg-emerald-500 text-white shadow-[0_4px_0_rgba(4,120,87,0.9)] hover:bg-emerald-600 disabled:opacity-60"
                  >
                    {editSaving ? t('common.pleaseWait', 'Please wait…') : t('common.save', 'Save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ✅ COUNTER MODAL */}
      {counterOpen && (
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCounterOpen(false)} />
          <div className="relative w-full sm:w-[520px] mx-3 rounded-[20px] bg-[#FFFCF7] dark:bg-[#101727] border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] shadow-[0_20px_0_0_rgba(15,23,42,0.35)] p-4">
            <div className="text-sm font-extrabold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
              {t('seller.counterOffer', 'Send counter-offer')}
            </div>

            <div className="mt-3 space-y-2">
              <label className="text-xs font-semibold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                {t('seller.counterAmount', 'Counter amount (USD, optional)')}
              </label>
              <input
                value={counterAmount}
                onChange={(e) => setCounterAmount(e.target.value)}
                placeholder="e.g. 25"
                className="w-full h-11 rounded-2xl px-3 bg-white dark:bg-slate-900 border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] text-sm"
              />

              <label className="text-xs font-semibold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                {t('seller.message', 'Message (optional)')}
              </label>
              <textarea
                value={counterMessage}
                onChange={(e) => setCounterMessage(e.target.value)}
                rows={4}
                placeholder="Write a quick note…"
                className="w-full rounded-2xl p-3 bg-white dark:bg-slate-900 border border-[var(--roddo-border-light)] dark:border-[var(--roddo-border-dark)] text-sm"
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                className="h-10 px-4 rounded-2xl bg-slate-100 dark:bg-slate-800 text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] text-sm font-semibold"
                onClick={() => setCounterOpen(false)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="h-10 px-4 rounded-2xl bg-[#7c6ff7] text-white text-sm font-extrabold shadow-[0_4px_0_0_rgba(124,111,247,0.35)] active:translate-y-[1px]"
                onClick={submitCounter}
              >
                {t('seller.send', 'Send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SellerDashboardView;
